import { useGRNAgainstTransferOrderAPI } from "@/app/hooks/api_hooks/Inventory/ClinicToClinicTransfer/useGRNAgainstTransferOrderAPI";
import { useTransferOrderDetailsAPI } from "@/app/hooks/api_hooks/Inventory/ClinicToClinicTransfer/useTransferOrderDetailsAPI";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { useUtils } from "@/app/hooks/useUtils";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import { IInventoryState } from "@/app_redux/reducers/slices/auth/inventory_slice";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useFeatureAccess } from "@/app/hooks/useFeatureAccess";

const TransferOrderDetails = () => {
  const { getRouteKey } = useRouter();
  const navigate = useNavigate();
  const { getStatusColor } = useUtils();
  const { toDetails } = useRouteInfo("INVENTORY")?.routeState?.state as IInventoryState;
  // RBAC: TRANSFER_ORDER_OMS_DASHBOARD_LINK — legacy role: central_ops
  const canSeeOmsDashboardLink = useFeatureAccess("TRANSFER_ORDER_OMS_DASHBOARD_LINK");
  // RBAC: TRANSFER_ORDER_CLINIC_GRN_ACTION — legacy roles: clinic_ops, clinic_ops_doggyworld
  const canPerformClinicGrnAction = useFeatureAccess("TRANSFER_ORDER_CLINIC_GRN_ACTION");
  const grnAgainstTransferOrder = useGRNAgainstTransferOrderAPI();
  const [grnButtonClicked, setGrnButtonClicked] = useState(false);
  const transferOrderDetails = useTransferOrderDetailsAPI();
  const { transfer_order_id } = useParams<{ transfer_order_id: string }>();
  const status = toDetails?.transfer_order_status?.toLowerCase();

  useEffect(() => {
    if (transfer_order_id) {
      transferOrderDetails(transfer_order_id);
    }
  }, [transfer_order_id]);

  return (
    <div className="rounded-md bg-white px-4 py-3">
      <h2 className="text-base font-semibold">Transfer Order</h2>
      <div className="flex justify-start items-baseline mt-5">
        <div className="flex justify-start items-center gap-1 p-2.5 w-[150px] h-[38px]">
          <span className="font-normal text-black text-xs">Receiver Clinic</span>
        </div>
        <p>{toDetails?.transfer_order_clinic_details?.receiver_clinic_name}</p>
      </div>
      <div className="flex justify-start items-baseline">
        <div className="flex justify-start items-center gap-1 p-2.5 w-[150px] h-[38px]">
          <span className="font-normal text-black text-xs">Sender Clinic</span>
        </div>
        <p>{toDetails?.transfer_order_clinic_details?.sender_clinic_name}</p>
      </div>
      <div className="flex justify-start items-baseline ">
        <div className="flex justify-start items-center gap-1 p-2.5 w-[150px] h-[38px]">
          <span className="font-normal text-black text-xs">TO Number</span>
        </div>
        <p>{toDetails?.transfer_order_number}</p>
      </div>
      <div className="flex justify-start items-baseline ">
        <div className="flex justify-start items-center gap-1 p-2.5 w-[150px] h-[38px]">
          <span className="font-normal text-black text-xs">TO Status</span>
        </div>
        <p
          className={` text-left whitespace-nowrap  text-xs font-medium  text-[${getStatusColor(toDetails?.transfer_order_status)?.color}] `}
        >
          {toDetails?.transfer_order_status}
        </p>
      </div>
      <hr />
      <h2 className="text-base font-semibold mt-5">Order List</h2>
      <div className="shadow-md mt-9">
        <table className="divide-y divide-gray-200 border rounded-md w-full">
          <thead className="bg-[rgb(244,249,254)] sticky top-0">
            <tr>
              <th className="px-3 py-3 text-left text-sm text-black font-medium">Product Name</th>
              <th className="px-3 py-3 text-left whitespace-nowrap text-sm text-black font-medium">Ordered Qty</th>
              <th className="px-3 py-3 text-left whitespace-nowrap text-sm text-black font-medium">Lot</th>
              <th className="px-3 py-3 text-left whitespace-nowrap text-sm text-black font-medium">Manufacturer</th>
              <th className="px-3 py-3 text-left whitespace-nowrap text-sm text-black font-medium">Expiry</th>
              <th className="px-3 py-3 text-left whitespace-nowrap text-sm text-black font-medium">MRP</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-orange-100">
            {toDetails?.transfer_order_data?.map((item, i) => (
              <tr key={i}>
                <td className={"px-3 py-4 text-xs font-normal text-left"}>
                  <p className="w-[235px]">{item?.variant_name}</p>
                </td>
                <td className={"px-3 py-4 text-xs font-normal text-left"}>{item?.ordered_qty ?? "-"}</td>
                <td className={"px-3 py-4 text-xs font-normal text-left"}>{item?.lot_number}</td>
                <td className={"px-3 py-4 text-xs font-normal text-left"}>{item?.manufacturer_name}</td>
                <td className={"px-3 py-4 text-xs font-normal text-left"}>{item?.expiry_date}</td>
                <td className={"px-3 py-4 text-xs font-normal text-left"}>{item?.mrp}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex fixed bottom-5 right-5 gap-x-3 p-3 rounded-md">
        {canSeeOmsDashboardLink && (
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              navigate(getRouteKey("ORDER_MANAGEMENT", "url"));
            }}
            type="button"
            className="bg-[#193A6A] cursor-pointer px-4 py-2 mt-5 rounded-md font-medium text-white text-sm"
          >
            Link to OMS Dashboard
          </button>
        )}
        {canPerformClinicGrnAction && toDetails?.show_grn_button && (
            <button
              onClick={async (e) => {
                e.preventDefault();
                e.stopPropagation();
                if (grnButtonClicked) return;
                setGrnButtonClicked(true);
                try {
                  await grnAgainstTransferOrder(toDetails);
                  navigate(`/app/inventory/pending-goods?to_id=${toDetails?.transfer_order_id}`);
                } catch (error) {
                  console.error("GRN TO failed:", error);
                }
              }}
              type="button"
              disabled={grnButtonClicked}
              className={`${
                grnButtonClicked ? "bg-gray-400 cursor-not-allowed" : "bg-[#193A6A] hover:bg-[#193A6A] cursor-pointer"
              } text-white text-sm font-medium px-5 py-2 rounded-md transition`}
            >
              GRN Against TO
            </button>
          )}
      </div>
    </div>
  );
};

export default TransferOrderDetails;
